'use client';

import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket,
  Check,
  X,
  Spinner,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { ymPromoApplied, tmrPromoApplied } from '@/lib/analytics';
import { useCartStore, selectPromoEligiblePrice } from '@/stores/cart-store';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/safe-storage';

// ========== TYPES ==========

interface PromoCodeResponse {
  id: number;
  code: string;
  name: string;
  product_id: number;
  /** Цена по промокоду: 0 — подарок, больше нуля — платная позиция. */
  price: number;
  is_paid: boolean;
  min_order_sum: number;
  start_date: string | null;
  end_date: string | null;
  product_name?: string;
  product_image?: string;
}

interface PromoCodeInputProps {
  className?: string;
}

// ========== HELPER FUNCTIONS ==========

const formatPrice = (price: number): string => {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
};

// ========== MAIN COMPONENT ==========

export function PromoCodeInput({ className }: PromoCodeInputProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Local state
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoProduct, setPromoProduct] = useState<ProductWithCategories | null>(null);
  
  // Cart Store
  const promoEligiblePrice = useCartStore(selectPromoEligiblePrice);
  const addPromoItem = useCartStore((state) => state.addPromoItem);
  const removePromoItem = useCartStore((state) => state.removePromoItem);
  const cartItems = useCartStore((state) => state.items);
  
  // Check if promo code is already applied (by checking cart items)
  useEffect(() => {
    const promoItem = cartItems.find(item => item.bonusType === 'promo');
    if (promoItem) {
      // Try to get promo code from localStorage (stored when applied)
      const storedPromoCode = safeGetItem('appliedPromoCode');
      setAppliedPromoCode(storedPromoCode || 'Промокод применен');
      setPromoProduct(promoItem.product as ProductWithCategories);
    } else {
      setAppliedPromoCode(null);
      setPromoProduct(null);
      safeRemoveItem('appliedPromoCode');
    }
  }, [cartItems]);

  // Если в корзине появился "Сет дня" — автоматически снимаем применённый промокод
  // (промокод и сет дня не суммируются)
  useEffect(() => {
    const hasSetOfTheDay = cartItems.some(
      (it) => (it.product as { is_set_of_the_day?: boolean }).is_set_of_the_day
    );
    const promoItem = cartItems.find(item => item.bonusType === 'promo');
    if (hasSetOfTheDay && promoItem) {
      removePromoItem();
      safeRemoveItem('appliedPromoCode');
      setError('Промокод снят: его нельзя применять вместе с «Сетом дня»');
    }
  }, [cartItems, removePromoItem]);
  
  // Validate and apply promo code
  const handleApplyPromo = useCallback(async () => {
    if (!inputValue.trim()) {
      setError('Введите промокод');
      return;
    }

    // Промокод нельзя применить если в корзине есть Сет дня
    const hasSetOfTheDay = cartItems.some(
      (it) => (it.product as { is_set_of_the_day?: boolean }).is_set_of_the_day
    );
    if (hasSetOfTheDay) {
      setError('Промокод нельзя применить вместе с «Сетом дня»');
      return;
    }

    setIsValidating(true);
    setError(null);
    
    try {
      // Call API to validate promo code
      const response = await apiClient.post<PromoCodeResponse>('/promo-codes/validate', {
        code: inputValue.trim().toUpperCase(),
      });
      
      // Check minimum order sum
      if (response.min_order_sum && promoEligiblePrice < response.min_order_sum) {
        const remaining = response.min_order_sum - promoEligiblePrice;
        setError(`Минимальная сумма заказа для этого промокода: ${formatPrice(response.min_order_sum)}. Добавьте товаров на ${formatPrice(remaining)}`);
        setIsValidating(false);
        return;
      }
      
      // Get product details
      let product: ProductWithCategories;
      try {
        product = await getProductById(response.product_id);
      } catch (err) {
        // If product fetch fails, create a minimal product object
        product = {
          id: response.product_id,
          name: response.product_name || 'Подарок по промокоду',
          description: '',
          price: 0,
          image_url: response.product_image || '',
          categories: [],
          is_available: true,
          status: 'available',
          spiciness: 'none',
          sku: '',
        } as ProductWithCategories;
      }
      
      // Цену диктует промокод, а не каталог: у виртуального товара промокода
      // цена нулевая, а сама акция может быть платной («ролл за 99 ₽»).
      // Сервер считает так же (orders.js, блок 5), поэтому суммы совпадут.
      product = { ...product, price: Number(response.price) || 0 };

      // Add product to cart as promo item (separate from status rolls)
      addPromoItem(product);
      
      // Store promo code and name in localStorage
      safeSetItem('appliedPromoCode', response.code);
      safeSetItem('appliedPromoName', response.name);
      
      setAppliedPromoCode(response.code);
      setPromoProduct(product);
      setInputValue('');
      setError(null);
      ymPromoApplied(response.code);
      tmrPromoApplied();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = error?.response?.data?.error || error?.message || 'Промокод не найден или недействителен';
      setError(errorMessage);
      setAppliedPromoCode(null);
      setPromoProduct(null);
    } finally {
      setIsValidating(false);
    }
  }, [inputValue, promoEligiblePrice, addPromoItem]);
  
  // Remove applied promo code
  const handleRemovePromo = useCallback(() => {
    // Remove promo item from cart
    removePromoItem();
    safeRemoveItem('appliedPromoCode');
    safeRemoveItem('appliedPromoName');
    setAppliedPromoCode(null);
    setPromoProduct(null);
    setInputValue('');
    setError(null);
  }, [removePromoItem]);
  
  return (
    <div className={cn('', className)}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center justify-between p-3 rounded-card transition-all',
          isDark 
            ? 'bg-[var(--surface-secondary)] hover:bg-[var(--surface-hover)]' 
            : 'bg-[var(--surface-secondary)] hover:bg-[var(--surface-tertiary)]',
          appliedPromoCode && (isDark ? 'bg-[var(--success)]/10' : 'bg-[var(--success-bg)]')
        )}
      >
        <div className="flex items-center gap-2">
          <Ticket weight="fill" className={cn(
            'w-5 h-5',
            appliedPromoCode 
              ? 'text-[var(--success)]' 
              : isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]'
          )} />
          <span className={cn(
            'text-sm font-medium',
            'text-[var(--text-primary)]'
          )}>
            {appliedPromoCode ? `Промокод: ${appliedPromoCode}` : 'У меня есть промокод'}
          </span>
          
          {appliedPromoCode && promoProduct && (
            <span className="text-xs font-bold text-[var(--success)] ml-1">
              Подарок добавлен
            </span>
          )}
        </div>
        
        {appliedPromoCode ? (
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleRemovePromo();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleRemovePromo();
              }
            }}
            className={cn(
              'p-1.5 rounded-pill transition-colors cursor-pointer',
              isDark 
                ? 'text-[var(--error)] hover:bg-[var(--error)]/10' 
                : 'text-[var(--error)] hover:bg-[var(--error-bg)]'
            )}
          >
            <X weight="bold" className="w-4 h-4" />
          </div>
        ) : (
          isExpanded 
            ? <CaretUp weight="bold" className={cn('w-4 h-4', isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]')} />
            : <CaretDown weight="bold" className={cn('w-4 h-4', isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]')} />
        )}
      </button>
      
      {/* Expandable Input Section */}
      <AnimatePresence>
        {isExpanded && !appliedPromoCode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3">
              <div className="flex gap-2">
                {/* Input */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value.toUpperCase());
                      setError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyPromo();
                      }
                    }}
                    placeholder="Введите промокод"
                    className={cn(
                      'w-full h-11 px-4 rounded-card text-[16px] font-medium transition-all',
                      'placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2',
                      isDark 
                        ? 'bg-[var(--surface-tertiary)] text-[var(--text-primary)] focus:ring-[var(--primary)]' 
                        : 'bg-[var(--surface-primary)] border border-[var(--border-default)] text-[var(--text-primary)] focus:ring-[var(--primary)] focus:border-transparent',
                      error && 'ring-2 ring-[var(--error)] border-transparent'
                    )}
                    disabled={isValidating}
                  />
                </div>
                
                {/* Apply Button */}
                <motion.button
                  onClick={handleApplyPromo}
                  disabled={isValidating || !inputValue.trim()}
                  className={cn(
                    'h-11 px-5 rounded-card font-medium text-sm flex items-center justify-center gap-2 transition-all',
                    'text-[var(--text-inverse)]',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    !isValidating && inputValue.trim() && 'hover:shadow-float'
                  )}
                  style={{
                    background: 'linear-gradient(90deg, var(--warning) 0%, var(--warning) 100%)'
                  }}
                  whileTap={!isValidating && inputValue.trim() ? { scale: 0.98 } : {}}
                >
                  {isValidating ? (
                    <Spinner weight="bold" className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check weight="bold" className="w-4 h-4" />
                      Применить
                    </>
                  )}
                </motion.button>
              </div>
              
              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="text-xs text-[var(--error)] mt-2 pl-1"
                  >
                    {error}
                  </motion.p>
                )}
              </AnimatePresence>
              
              {/* Help Text */}
              <p className={cn(
                'text-xs mt-2 pl-1',
                isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-tertiary)]'
              )}>
                Введите промокод для получения подарка
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

